import { api } from '@/lib/api';
import type {
  Sensor,
  SensorAccountFilterOption,
  SensorAccountFilterPage,
  SensorStreamOption,
  SensorStreamOptionPage,
  SensorsFilters,
  SensorsResponse,
  SensorEditData,
  SensorEditOptions,
  SensorEditForm,
  SensorCreateForm,
  DailyLogForm,
  InvoiceSearchResult,
} from './types';
import { ATEK_ORG_ID } from '@/features/users/utils/constants';

// =============================================================================
// V4 API Response Types
// =============================================================================

interface V4SensorsListResponse {
  sensors: Array<{
    id: string;
    mongoSensorId: string;
    name: string;
    status: number;
    billable: boolean;
    lastReadingAt: string | null;
    currentValue: string | null;
    sensorType: string | null;
    sensorTypeName: string | null;
    sensorTypeUnit: string | null;
    usergroupName: string | null;
    streamName: string | null;
    customId: string | null;
    inputNumber: string | null;
    tags: string[];
    account: {
      id: string;
      name: string;
      accountNumber: string;
      mongoOrganisationId: string;
    } | null;
    site: { id: string; name: string } | null;
    location: { id: string; name: string } | null;
    imageUrl: string | null;
  }>;
  total: number;
  pageNumber: number;
  pageLength: number;
}

interface V4AccountsResponse {
  accounts: Array<{
    id: string;
    name: string;
    accountNumber: string | null;
  }>;
  pageNumber?: number;
  pageLength?: number;
  total?: number;
}

interface V4StreamsResponse {
  streams: Array<{
    mongoStreamId: string;
    name: string | null;
    mongoTransmitterId: string | null;
    transmitterName: string | null;
  }>;
  pageNumber?: number;
  pageLength?: number;
  total?: number;
}

type V2StreamLookupResponse =
  | Array<{
      _id: string;
      name?: string | null;
      transmitter?: string | { _id?: string } | null;
    }>
  | {
      _id: string;
      name?: string | null;
      transmitter?: string | { _id?: string } | null;
    };

// =============================================================================
// API Functions
// =============================================================================

/**
 * Search PostgreSQL accounts for the Sensors account filter.
 */
function transformSensorAccountFilterOption(
  account: { id: string; name: string; accountNumber: string | null },
): SensorAccountFilterOption {
  return {
    _id: account.id,
    name: account.name,
    accountNumber: account.accountNumber,
  };
}

export async function searchSensorAccountsPaginated(params: {
  search: string;
  pageNumber: number;
  pageLength: number;
}): Promise<SensorAccountFilterPage> {
  const response = await api.get<V4AccountsResponse>('/v4/accounts', {
    search: params.search,
    pageLength: params.pageLength,
    pageNumber: params.pageNumber,
    sortKey: 'name',
    sortDirection: 1,
  });

  return {
    accounts: (response.accounts || []).map(transformSensorAccountFilterOption),
    pageNumber: response.pageNumber ?? params.pageNumber,
    pageLength: response.pageLength ?? params.pageLength,
    total: response.total ?? response.accounts.length,
  };
}

export async function getSensorAccountById(
  accountId: string,
): Promise<SensorAccountFilterOption | null> {
  const response = await api.get<{
    account?: { id: string; name: string; accountNumber: string | null };
  }>(`/v4/accounts/${accountId}`);

  return response.account
    ? transformSensorAccountFilterOption(response.account)
    : null;
}

function transformSensorStreamOption(stream: {
  mongoStreamId: string;
  name?: string | null;
  mongoTransmitterId?: string | null;
  transmitterName?: string | null;
}): SensorStreamOption {
  return {
    _id: stream.mongoStreamId,
    name: stream.name || stream.mongoStreamId,
    transmitter: stream.mongoTransmitterId || null,
    transmitterName: stream.transmitterName || null,
  };
}

function extractReferenceId(ref: string | { _id?: string } | null | undefined): string | null {
  if (!ref) return null;
  if (typeof ref === 'string') return ref;
  return ref._id || null;
}

export async function searchSensorStreamsPaginated(params: {
  search: string;
  pageNumber: number;
  pageLength: number;
  organisationId?: string | null;
  currentSensorId?: string | null;
}): Promise<SensorStreamOptionPage> {
  const response = await api.get<V4StreamsResponse>('/v4/streams', {
    search: params.search,
    pageLength: params.pageLength,
    pageNumber: params.pageNumber,
    sortKey: 'name',
    sortDirection: 1,
    organisationId: params.organisationId,
    availableForSensorId: params.currentSensorId,
    unassignedOnly: params.currentSensorId ? undefined : 'true',
  });

  return {
    streams: (response.streams || []).map(transformSensorStreamOption),
    pageNumber: response.pageNumber ?? params.pageNumber,
    pageLength: response.pageLength ?? params.pageLength,
    total: response.total ?? (response.streams || []).length,
  };
}

export async function getSensorStreamById(
  streamId: string,
): Promise<SensorStreamOption | null> {
  const response = await api.get<V2StreamLookupResponse>(`/v4/legacy/v2/streams/${streamId}`, {
    select: 'name transmitter',
  });
  const stream = Array.isArray(response) ? response[0] : response;
  if (!stream?._id) return null;

  return {
    _id: stream._id,
    name: stream.name || stream._id,
    transmitter: extractReferenceId(stream.transmitter),
    transmitterName: null,
  };
}

/**
 * Get sensors with filtering, pagination, and sorting.
 * Reads from V4/PostgreSQL. Transforms response to match Sensor interface.
 */
export async function getSensors(
  filters: SensorsFilters = {},
): Promise<SensorsResponse> {
  const params: Record<string, string | number> = {};

  // Column filters are server-side on this page.
  if (filters.name) {
    params.name = filters.name;
  }

  if (filters.streamName) {
    params.streamName = filters.streamName;
  }

  if (filters.siteLocation) {
    params.siteLocation = filters.siteLocation;
  }

  if (filters.currentValue) {
    params.currentValue = filters.currentValue;
  }

  if (filters.sensorTypeName) {
    params.sensorTypeName = filters.sensorTypeName;
  }

  if (filters.usergroupName) {
    params.usergroupName = filters.usergroupName;
  }

  if (filters.customId) {
    params.customId = filters.customId;
  }

  if (filters.inputNumber) {
    params.inputNumber = filters.inputNumber;
  }

  if (filters.tags) {
    params.tags = filters.tags;
  }

  if (filters.lastReadingFrom) {
    params.lastReadingFrom = filters.lastReadingFrom;
  }

  if (filters.lastReadingTo) {
    params.lastReadingTo = filters.lastReadingTo;
  }

  // Status filter
  if (filters.status_in && filters.status_in.length > 0) {
    params.status_in = JSON.stringify(filters.status_in);
  }

  // Account filter
  if (filters.accountId) {
    params.accountId = filters.accountId;
  }

  // Billable filter
  if (filters.billable !== undefined) {
    params.billable = String(filters.billable);
  }

  // Has image filter
  if (filters.hasImage !== undefined) {
    params.hasImage = String(filters.hasImage);
  }

  // Include archived sensors
  params.showarchived = 'true';

  // Pagination
  params.pageNumber = filters.pageNumber ?? 0;
  params.pageLength = filters.pageLength ?? 50;

  // Sorting
  if (filters.sortKey) {
    params.sortKey = filters.sortKey;
  }
  if (filters.sortDirection !== undefined) {
    params.sortDirection = filters.sortDirection;
  }

  const response = await api.get<V4SensorsListResponse & { status?: boolean; msg?: string }>(
    '/v4/sensors',
    params,
  );

  // Permission gates answer HTTP 200 {status:false, msg} — surface that as an
  // error instead of rendering a silently empty table.
  if (!Array.isArray(response.sensors)) {
    throw new Error(response.msg || 'Unexpected /v4/sensors response: missing sensors list');
  }

  // Transform V4 response to match Sensor interface
  const sensors: Sensor[] = response.sensors.map((s) => ({
    _id: s.mongoSensorId,
    name: s.name,
    status: s.status as Sensor['status'],
    value: s.currentValue,
    unit: s.sensorTypeUnit,
    timestamp: s.lastReadingAt,
    organisation: s.account
      ? { _id: s.account.mongoOrganisationId, name: s.account.name }
      : null,
    accountId: s.account?.id ?? null,
    site: s.site ? { _id: s.site.id, name: s.site.name } : null,
    location: s.location
      ? { _id: s.location.id, name: s.location.name }
      : null,
    sensortype: s.sensorTypeName
      ? { _id: s.sensorType || '', name: s.sensorTypeName }
      : null,
    usergroup: s.usergroupName
      ? { _id: '', name: s.usergroupName }
      : null,
    stream_name: s.streamName,
    billable: s.billable,
    customid: s.customId,
    input_number: s.inputNumber,
    tags: s.tags || [],
    imageUrl: s.imageUrl,
  }));

  return {
    sensors,
    total: response.total || 0,
    pageNumber: response.pageNumber,
    pageLength: response.pageLength,
  };
}

// =============================================================================
// Edit Sensor API (V2/MongoDB for full field coverage)
// =============================================================================

interface SensorEditResponse {
  sensor: SensorEditData;
  options: SensorEditOptions;
}

interface V4SensorDetailResponse {
  success: boolean;
  sensor: {
    id: string;
    mongoSensorId: string;
    name: string;
    status: number;
    billable: boolean;
    lastReadingAt: string | null;
    firstDataAt: string | null;
    currentValue: string | number | null;
    sensorType: string | null;
    sensorTypeName: string | null;
    equipmentType: string | null;
    manufacturer: string | null;
    model: string | null;
    serialNumber: string | null;
    archived: boolean;
    archivedAt: string | null;
    /** #1182 — raw per-sensor override; null means "inherit the account tag". */
    calibrationIncluded: boolean | null;
    /** #1182 — resolved server-side: the override, or what the account tag implies. */
    calibrationIncludedEffective: boolean;
    /** #1118 — raw per-sensor on-site exception; null means "follow the account". */
    onsiteCalibrationOverride: 'onsite' | 'ship' | null;
    maintenance: boolean;
    inputNumber: string | null;
    customId: string | null;
    description: string | null;
    tags: string[];
    invoices: string[];
    quotes: string[];
    subscriptions: string[];
    sensorgroups: Array<{
      id: string;
      mongoSensorgroupId: string;
      name: string;
    }>;
    streamName: string | null;
    mongoStreamId: string | null;
    usergroupName: string | null;
    mongoUsergroupId: string | null;
    mongoOrganisationId: string | null;
    account: {
      id: string;
      name: string;
      accountNumber: string;
    } | null;
    site: {
      id: string;
      name: string;
      mongoSiteId: string | null;
    } | null;
    location: {
      id: string;
      name: string;
      mongoLocationId: string | null;
    } | null;
  };
}

interface V4SensorFormOptionsResponse {
  sensortypes: Array<{ _id: string; name: string }>;
  usergroups: Array<{ _id: string; name: string }>;
  sites: Array<{ _id: string; name: string }>;
  locations: Array<{ _id: string; name: string; site?: string }>;
  streams: Array<{ _id: string; name: string; transmitter?: string }>;
  transmitters: Array<{ _id: string; name: string; dev_id?: string }>;
  sensorgroups?: Array<{ _id: string; name: string }>;
}

function mapV4ToEditData(v4: V4SensorDetailResponse['sensor']): SensorEditData {
  return {
    _id: v4.mongoSensorId,
    name: v4.name,
    customid: v4.customId || undefined,
    input_number: v4.inputNumber ?? undefined,
    status: v4.status,
    billable: v4.billable,
    maintenance: v4.maintenance,
    archived: v4.archived,
    // #1182 — the checkbox shows the EFFECTIVE value (resolved server-side from the
    // account Calibration tag when no per-sensor override is stored).
    calibrationIncluded: v4.calibrationIncludedEffective,
    calibrationIsInherited: v4.calibrationIncluded == null,
    onsiteCalibrationOverride: v4.onsiteCalibrationOverride ?? null,
    timestamp: v4.lastReadingAt || undefined,
    first_data_at: v4.firstDataAt || undefined,
    stream: v4.mongoStreamId || undefined,
    stream_name: v4.streamName || undefined,
    sensortype: v4.sensorType || undefined,
    usergroup: v4.mongoUsergroupId || undefined,
    site: v4.site?.mongoSiteId || undefined,
    location: v4.location?.mongoLocationId || undefined,
    manufacturer: v4.manufacturer || undefined,
    model: v4.model || undefined,
    serialnumber: v4.serialNumber || undefined,
    tags: v4.tags,
    description: v4.description || undefined,
    invoices: v4.invoices,
    sensorgroups: v4.sensorgroups.map((group) => group.mongoSensorgroupId),
    organisation: v4.mongoOrganisationId || undefined,
    value: v4.currentValue,
  };
}

/**
 * Load sensor data + dropdown options from V4/PostgreSQL endpoints.
 * Writes still go through V2/MongoDB.
 */
export async function getSensorEdit(sensorId: string): Promise<SensorEditResponse> {
  const [sensorResponse, options] = await Promise.all([
    api.get<V4SensorDetailResponse>(`/v4/sensors/${sensorId}`),
    api.get<V4SensorFormOptionsResponse>('/v4/sensors/form-options', { sensorId }),
  ]);
  return {
    sensor: mapV4ToEditData(sensorResponse.sensor),
    options,
  };
}

/** Save sensor changes (diff-only body) */
export async function updateSensor(
  sensorId: string,
  changes: Partial<SensorEditForm>,
): Promise<void> {
  // #1182 / #1118 — the calibration flags are PG-native and do NOT go through the
  // legacy Mongo bridge: they have their own V4 route, so the write lands in the same
  // store the edit dialog reads back from. Peel them off before the legacy PUT and send
  // them together in a single PATCH.
  const { calibrationIncluded, onsiteCalibrationOverride, ...legacyChanges } = changes;
  const calibrationFlags: Record<string, unknown> = {};
  if (calibrationIncluded !== undefined) calibrationFlags.calibrationIncluded = calibrationIncluded;
  if (onsiteCalibrationOverride !== undefined) {
    // 'inherit' is a UI-only sentinel; the column stores NULL for "follow the account".
    calibrationFlags.onsiteCalibrationOverride = onsiteCalibrationOverride === 'inherit'
      ? null
      : onsiteCalibrationOverride;
  }
  if (Object.keys(calibrationFlags).length > 0) {
    await api.patch(`/v4/sensors/${sensorId}/calibration-flags`, calibrationFlags);
  }
  if (Object.keys(legacyChanges).length === 0) return;

  // MongoDB expects null (not empty string) for ObjectId fields when clearing
  const OBJECTID_FIELDS = ['location', 'site', 'sensortype', 'usergroup', 'stream'] as const;
  const sanitized: Record<string, unknown> = { ...legacyChanges };
  for (const field of OBJECTID_FIELDS) {
    if (sanitized[field] === '') {
      sanitized[field] = null;
    }
  }
  // Serialize dailylog form strings to numbers
  if (legacyChanges.dailylog) {
    const dailylog = serializeDailyLog(legacyChanges.dailylog);
    sanitized.dailylog = dailylog || { am: null, pm: null, threshold_min: null, threshold_max: null };
  }
  await api.put(`/v4/legacy/v2/sensors/${sensorId}`, sanitized);
}

/** Fetch dropdown options for create sensor form */
export async function getSensorCreateOptions(
  organisationId?: string | null,
): Promise<SensorEditOptions> {
  return organisationId
    ? api.get<V4SensorFormOptionsResponse>('/v4/sensors/form-options', { organisationId })
    : api.get<V4SensorFormOptionsResponse>('/v4/sensors/form-options');
}

/** Convert DailyLogForm strings to numbers for the API (null for empty) */
function serializeDailyLog(form: DailyLogForm): Record<string, number | null> | undefined {
  const am = form.am !== '' ? Number(form.am) : null;
  const pm = form.pm !== '' ? Number(form.pm) : null;
  const threshold_min = form.threshold_min !== '' ? Number(form.threshold_min) : null;
  const threshold_max = form.threshold_max !== '' ? Number(form.threshold_max) : null;
  // Only include if at least one field is set
  if (am === null && pm === null && threshold_min === null && threshold_max === null) return undefined;
  return { am, pm, threshold_min, threshold_max };
}

/** Create a new sensor via V2 (MongoDB) */
export async function createSensor(
  data: SensorCreateForm,
): Promise<{ _id: string }> {
  // Strip empty strings for ObjectId fields — backend expects them to be absent
  const OBJECTID_FIELDS = ['location', 'site', 'sensortype', 'usergroup'] as const;
  const sanitized: Record<string, unknown> = { ...data };
  for (const field of OBJECTID_FIELDS) {
    if (!sanitized[field]) delete sanitized[field];
  }
  // Remove empty optional strings
  if (!sanitized.customid) delete sanitized.customid;
  if (!sanitized.input_number) delete sanitized.input_number;
  if (!sanitized.manufacturer) delete sanitized.manufacturer;
  if (!sanitized.model) delete sanitized.model;
  if (!sanitized.serialnumber) delete sanitized.serialnumber;
  if (!sanitized.description) delete sanitized.description;
  if (!sanitized.organisation) delete sanitized.organisation;
  if (Array.isArray(sanitized.tags) && (sanitized.tags as string[]).length === 0) delete sanitized.tags;
  if (Array.isArray(sanitized.invoices) && (sanitized.invoices as string[]).length === 0) delete sanitized.invoices;
  if (Array.isArray(sanitized.sensorgroups) && (sanitized.sensorgroups as string[]).length === 0) delete sanitized.sensorgroups;

  // Serialize dailylog form strings to numbers
  const dailylog = serializeDailyLog(data.dailylog);
  if (dailylog) {
    sanitized.dailylog = dailylog;
  } else {
    delete sanitized.dailylog;
  }

  return api.post<{ _id: string }>('/v4/legacy/v2/sensors', sanitized);
}

/** Search invoices by number (ATEK org only) */
export async function searchInvoices(query: string): Promise<InvoiceSearchResult[]> {
  const response = await api.get<{ invoices: InvoiceSearchResult[] }>('/v4/legacy/invoices', {
    invoice_number: query,
    organisation: ATEK_ORG_ID,
  });
  return Array.isArray(response.invoices) ? response.invoices : [];
}
